import { EntryType, type EntryItemType } from '@morph-mapper/types';
import type { StoredSuggestion, StoreSlice, SuggestedChange, SuggestedGraph } from '@/store';
import { suggestionKey } from '../../../../store/ai-suggestions';
import { GraphRefType } from '../../../graph-editor/types';
import { TERMINAL_NODE_ID } from '../../../graph-editor/config/graph';
import { createGraphStore } from '../../../graph-editor/store';
import {
  createReplaceGraphCommand,
  type ReplaceGraphCommandProps,
} from '../../../graph-editor/lib/commands';
import { asYesNo, type ResolvedChange } from './change-cards';
import { blocksFor, graphParts } from './graph-change';

// The whole store, as the graph editor's commands need it. A change itself uses
// the entries, the AI suggestions and, for a graph, the graphs and file type.
type Store = StoreSlice;

/** What a field had before a change was applied, to put back on Undo. */
export type AppliedChange = {
  entryId: string;
  previousType: EntryItemType;
  /** The column, value, graph or code the field had. */
  previous: unknown;
  /**
   * For a graph: the graph the change went into, whether it was made for the
   * change, and the nodes and edges it had before.
   */
  graph?: { graphId: string; created: boolean; before: ReplaceGraphCommandProps };
  /** For an AI suggestion: the one applied, and whether it was marked applied before. */
  suggestion?: { applied: StoredSuggestion; wasApplied: boolean };
};

const HISTORY_LABEL = 'Mapping assistant';

/** Replaces a graph's nodes and edges with the graph editor's own command. */
const replaceGraph = (store: Store, graphId: string, parts: ReplaceGraphCommandProps) => {
  const result = createGraphStore(store, { id: graphId })
    .getState()
    .cmd.applyCommand(createReplaceGraphCommand(parts), HISTORY_LABEL);
  if (result.isErr()) throw new Error(result.error.message);
};

/**
 * Gives a graph field the assistant's graph. A field without a graph gets one
 * first, like opening its graph editor does; the graph is then replaced with
 * the editor's command, so it is in the graph's history and the field
 * recalculates like after an edit.
 */
const applyGraph = (store: Store, entryId: string, suggested: SuggestedGraph) => {
  const { entries, graphs } = store;
  let graphId = entries.entry(entryId, EntryType.Graph).getGraphId();
  const created = !graphId;
  if (!graphId) {
    graphId = graphs.init({ ref: { kind: GraphRefType.Field, fieldId: entryId } });
    entries.linkGraph(entryId, graphId);
  }

  const graph = graphs.get(graphId);
  const before = {
    nodes: Object.values(graph.nodes).filter((node) => node.id !== TERMINAL_NODE_ID),
    edges: Object.values(graph.edges),
  };
  try {
    const blocks = blocksFor(store.config.getVariant());
    replaceGraph(store, graphId, graphParts(suggested, blocks, graph));
  } catch (error) {
    if (created) graphs.deleteGraph(graphId);
    throw error;
  }
  return { graphId, created, before };
};

/**
 * Applies a suggested change with the same store calls the editors use: the
 * field switches type first when the change needs it, then gets its column,
 * value, suggestion or graph, and recalculates as if the user had made the
 * change. When it can't be applied, the field is put back and it throws.
 */
export const applyChange = (
  store: Store,
  entryId: string,
  change: SuggestedChange,
  resolved: ResolvedChange
): AppliedChange => {
  const { entries } = store;
  const previousType = entries.getType(entryId) as EntryItemType;
  const previous = entries.getUnderlyingValue(entryId);
  const targetType = resolved.targetType ?? previousType;
  if (previousType !== targetType) entries.setType(entryId, targetType);

  if (change.kind === 'column') {
    entries.entry(entryId, EntryType.Cell).setColumn(change.value);
  } else if (change.kind === 'value') {
    if (targetType === EntryType.Boolean) {
      entries.entry(entryId, EntryType.Boolean).setBoolean(asYesNo(change.value) ?? false);
    } else {
      entries.entry(entryId, EntryType.Simple).setLiteral(String(change.value));
    }
  } else if (change.kind === 'graph') {
    try {
      return { entryId, previousType, previous, graph: applyGraph(store, entryId, change.value) };
    } catch (error) {
      undoChange(store, { entryId, previousType, previous });
      throw error;
    }
  } else if (resolved.suggestion) {
    // The same as Apply under AI Suggestions: code keeps its snippet.
    const { suggestion } = resolved;
    entries.setTemplateValue(
      entryId,
      suggestion.type === EntryType.Code ? suggestion.snippet : suggestion.value
    );
    store.ai.markSuggestionApplied(entryId, suggestion);
    return {
      entryId,
      previousType,
      previous,
      suggestion: { applied: suggestion, wasApplied: suggestion.applied },
    };
  }

  return { entryId, previousType, previous };
};

/**
 * Takes the applied mark off a suggestion again. An applied suggestion is kept
 * through every re-evaluation, so after Undo it would stay when it shouldn't.
 */
const unmarkSuggestion = (store: Store, entryId: string, applied: StoredSuggestion) => {
  const key = suggestionKey(applied);
  const list = store.ai.suggestions[entryId] ?? [];
  store.ai.replaceSuggestions(
    entryId,
    list.map((s) => (suggestionKey(s) === key ? { ...s, applied: false } : s))
  );
};

/**
 * Puts back the type and the column, value, graph or code the field had. A
 * graph the change went into gets its nodes and edges back, with the editor's
 * command again; a graph made for the change goes. An AI suggestion that Apply
 * marked as applied is no longer marked.
 */
export const undoChange = (
  store: Store,
  { entryId, previousType, previous, graph, suggestion }: AppliedChange
) => {
  const { entries } = store;
  if (graph && !graph.created) replaceGraph(store, graph.graphId, graph.before);

  if (entries.getType(entryId) !== previousType) entries.setType(entryId, previousType);
  entries.setUnderlyingValue(entryId, previous);
  if (graph?.created) store.graphs.deleteGraph(graph.graphId);
  if (suggestion && !suggestion.wasApplied) unmarkSuggestion(store, entryId, suggestion.applied);
};
